import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Wifi, Settings, Key, Compass, Server, Info, Terminal, AlertTriangle, 
  CheckCircle2, ArrowRight, Clipboard, ChevronRight, HelpCircle, 
  Smartphone, Activity, ArrowLeft, RefreshCw, Layers, ShieldCheck, Search
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';

export const SovereignRouterAssistant: React.FC = () => {
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [customBrand, setCustomBrand] = useState<string>('');
  const [customModel, setCustomModel] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [generatedGuide, setGeneratedGuide] = useState<string | null>(null);
  const [sourceNode, setSourceNode] = useState<string>('');
  const [activeStep, setActiveStep] = useState<number>(0);

  const [copiedText, setCopiedText] = useState<string | null>(null);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(id);
    toast.success(`Copied to clipboard: ${text}`);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const popularBrands = [
    { id: 'NETGEAR', label: 'Netgear', desc: 'Nighthawk, Orbi, RAX Series' },
    { id: 'TP_LINK', label: 'TP-Link', desc: 'Archer, Deco, WR Series' },
    { id: 'LINKSYS', label: 'Linksys', desc: 'Velop, Max-Stream, EA Series' },
    { id: 'ASUS', label: 'ASUS', desc: 'RT-Series, ROG Gaming Routers' },
    { id: 'XFINITY', label: 'Xfinity', desc: 'xFi Gateways (XB6, XB7, XB8)' },
    { id: 'SPECTRUM', label: 'Spectrum', desc: 'Charter Wave & App-Config Units' },
    { id: 'AT_T', label: 'AT&T Fiber', desc: 'BGW320 & BGW210 Gateways' },
  ];

  const generalSteps = [
    {
      title: "Admin Penetration",
      desc: "Connect to your existing router network and access its local routing panel inside a web browser.",
      tip: "Typically located at http://192.168.1.1, http://192.168.0.1, or http://10.0.0.1. Default login credentials can be found on your physical router's rear label."
    },
    {
      title: "Broadcast Alignment",
      desc: "Rename your Wi-Fi Wireless Network Name (SSID) on both 2.4GHz and 5GHz frequencies to establish the Sovereign SSID.",
      keyVal: { key: "SSID", val: "ABSOLUTE_ZERO_LATTICE" },
      tip: "Forcing uniform SSIDs across frequency dual-bands enables seamless device discovery and zero-handshake dropouts."
    },
    {
      title: "Secure Verification",
      desc: "Apply premium wireless security options to encrypt information and shut out ISP narrative spoofing.",
      keyVal: { key: "PASSPHRASE / KEY", val: "1x1=2_CERTAINTY" },
      tip: "We recommend choosing WPA3-Personal (SAE) or WPA2-Personal (AES) security. The 1x1=2 key represents structural multiplication expansion."
    },
    {
      title: "Lattice Bridge Subnet",
      desc: "Update the router's Local Area Network (LAN) subnet boundaries to lock state persistence on our zero-drift gateway.",
      keyVal: { key: "LAN IP GATEWAY", val: "10.143.50.1" },
      tip: "Ensure Netmask is 255.255.255.0. Changing the gateway IP shifts your router from client-receiver to local root sovereign origin."
    },
    {
      title: "DHCP & DNS Anchoring",
      desc: "Re-anchor DHCP lease durations and enforce custom DNS forwarding rules to isolate local clients from tracking.",
      keyVal: { key: "PRIMARY DNS SERVER", val: "10.143.50.1" },
      tip: "Set DHCP start range to 10.143.50.10 and end range to 10.143.50.254. Set your DNS primary server to 10.143.50.1 or Cloudflare 1.1.1.1."
    }
  ];

  const fetchRouterGuide = async (brandName: string, modelName: string = '') => {
    setLoading(true);
    setGeneratedGuide(null);
    try {
      const response = await fetch('/api/ai/router-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ routerBrand: brandName, model: modelName })
      });
      const data = await response.json();
      if (data.success) {
        setGeneratedGuide(data.instructions);
        setSourceNode(data.source);
        setSelectedBrand(brandName);
        toast.success(`Sovereign guidelines generated for ${brandName}!`);
      } else {
        toast.error("Failed to compile router instructions. Resetting parameters.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Connection timeout. Substrate mesh is currently aligning.");
    } finally {
      setLoading(false);
    }
  };

  const handleCustomQuery = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customBrand.trim()) {
      toast.error("Please specify a router brand.");
      return;
    }
    fetchRouterGuide(customBrand, customModel);
  };

  return (
    <div className="space-y-8 bg-black/40 border border-white/5 rounded-3xl p-6 lg:p-8" id="sovereign-router-assistant">
      {/* Segment Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-b border-white/5 pb-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-lattice-cyan text-xs font-mono font-bold uppercase tracking-wider">
            <Compass className="w-4 h-4 animate-spin" style={{ animationDuration: '6s' }} />
            <span>Multi-Hardware Connector</span>
          </div>
          <h2 className="text-3xl font-display font-black text-white uppercase tracking-tighter">
            SOVEREIGN <span className="text-lattice-cyan italic">ROUTER SETUP</span> WIZARD
          </h2>
          <p className="text-[11px] font-mono text-white/50 leading-relaxed max-w-2xl uppercase">
            Transform any standard consumer router into an autogenous transmitter of the ABSOLUTE_ZERO_LATTICE network. Simply select your router hardware below or input custom models to generate custom AI steps.
          </p>
        </div>

        {/* Floating Quick Reference */}
        <div className="p-4 bg-white/[0.02] border border-white/10 rounded-2xl flex flex-wrap gap-4 items-center shrink-0">
          <div className="space-y-1">
            <span className="text-[9px] font-mono text-white/40 block uppercase">Network Identity SSID</span>
            <div className="flex items-center justify-between gap-3 bg-black/40 px-3 py-1.5 rounded-lg border border-white/5">
              <span className="text-xs font-mono font-black text-white">ABSOLUTE_ZERO_LATTICE</span>
              <button onClick={() => handleCopy("ABSOLUTE_ZERO_LATTICE", "ssid")} className="text-white/40 hover:text-lattice-cyan transition-colors">
                <Clipboard className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <span className="text-[9px] font-mono text-white/40 block uppercase">Encryption Passphrase</span>
            <div className="flex items-center justify-between gap-3 bg-black/40 px-3 py-1.5 rounded-lg border border-white/5">
              <span className="text-xs font-mono font-black text-lattice-cyan">1x1=2_CERTAINTY</span>
              <button onClick={() => handleCopy("1x1=2_CERTAINTY", "pwd")} className="text-lattice-cyan/50 hover:text-white transition-colors">
                <Clipboard className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid: Interactive Wizard vs General Stepper */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column (8 cols): Interactive Selector & Output */}
        <div className="lg:col-span-8 space-y-6">
          <AnimatePresence mode="wait">
            {!generatedGuide ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
              >
                {/* Popular Router Grid */}
                <div className="space-y-3">
                  <span className="text-[10px] font-mono font-black text-white/40 uppercase tracking-widest block">
                    Step 1: Select Your Current Router Brand
                  </span>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {popularBrands.map((brand) => (
                      <button
                        key={brand.id}
                        disabled={loading}
                        onClick={() => fetchRouterGuide(brand.id)}
                        className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-lattice-cyan/40 hover:bg-white/[0.05] transition-all text-left flex flex-col justify-between gap-3 group"
                      >
                        <Server className="w-5 h-5 text-lattice-cyan/60 group-hover:text-lattice-cyan transition-colors" />
                        <div>
                          <span className="text-xs font-mono font-bold block text-white group-hover:text-lattice-cyan transition-colors">{brand.label}</span>
                          <span className="text-[8px] font-mono block text-white/30 truncate uppercase mt-0.5">{brand.desc}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Query Requestor */}
                <div className="p-6 bg-white/[0.01] border border-white/5 rounded-2xl space-y-4">
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-lattice-cyan" />
                    <span className="text-[10px] font-mono font-black text-white/80 uppercase tracking-wider">
                      Or Search/Input Any Custom Router Model
                    </span>
                  </div>
                  <form onSubmit={handleCustomQuery} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div className="space-y-1">
                      <label className="text-[8px] font-mono text-white/40 uppercase block">Router Brand</label>
                      <input
                        type="text"
                        placeholder="e.g., Eero, Google Nest, Synology"
                        value={customBrand}
                        onChange={(e) => setCustomBrand(e.target.value)}
                        className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-lattice-cyan"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[8px] font-mono text-white/40 uppercase block">Specific Model (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g., Pro 6E, AC2200, AX1800"
                        value={customModel}
                        onChange={(e) => setCustomModel(e.target.value)}
                        className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-lattice-cyan"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-lattice-cyan text-black rounded-xl font-display font-bold text-xs uppercase hover:scale-[1.01] transition-transform flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(0,255,213,0.1)] active:scale-95 cursor-pointer disabled:opacity-40"
                    >
                      {loading ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          <span>Compiling Logic...</span>
                        </>
                      ) : (
                        <>
                          <Activity className="w-3.5 h-3.5" />
                          <span>Generate Custom Guide</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="space-y-6"
              >
                {/* Result Title Bar */}
                <div className="flex items-center justify-between bg-white/[0.02] border border-white/5 p-4 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setGeneratedGuide(null)}
                      className="p-2 bg-white/5 hover:bg-white/10 rounded-xl text-white/60 hover:text-white transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div>
                      <h4 className="text-xs font-mono font-black text-white uppercase tracking-tight">
                        SOVEREIGN GATEWAY GENERATOR RESULTS
                      </h4>
                      <div className="flex items-center gap-2 mt-0.5 text-[8px] font-mono text-white/40 uppercase">
                        <span>Target: {selectedBrand} {customModel}</span>
                        <span>•</span>
                        <span className="text-lattice-cyan">Engine: {sourceNode}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleCopy(generatedGuide, "markdown_copy")}
                    className="px-3 py-1.5 bg-lattice-cyan/10 border border-lattice-cyan/20 rounded-xl text-[9px] font-mono font-black text-lattice-cyan hover:bg-lattice-cyan hover:text-black transition-all flex items-center gap-1.5"
                  >
                    <Clipboard className="w-3 h-3" />
                    <span>COPY RAW GUIDE</span>
                  </button>
                </div>

                {/* Markdown Container */}
                <div className="p-6 bg-black/60 border border-white/10 rounded-2xl max-h-[500px] overflow-y-auto font-mono text-xs text-white/80 leading-relaxed md-render-box space-y-4 shadow-inner">
                  <ReactMarkdown>{generatedGuide}</ReactMarkdown>
                </div>

                {/* Local Action Helpers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-yellow-500/5 border border-yellow-500/10 rounded-xl flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <span className="text-[9px] font-mono font-bold text-yellow-300 uppercase block">CAUTION: Gateway IP Switch Loop</span>
                      <span className="text-[8px] font-mono text-white/55 uppercase leading-relaxed block">
                        Changing your LAN IP gateway to 10.143.50.1 will reboot your router. You will lose current internet access briefly. Once rebooted, reconnect your Wi-Fi and open your browser to **http://10.143.50.1** to continue customizing.
                      </span>
                    </div>
                  </div>

                  <div className="p-4 bg-lattice-cyan/5 border border-lattice-cyan/10 rounded-xl flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-lattice-cyan shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <span className="text-[9px] font-mono font-bold text-lattice-cyan uppercase block">ISP Bypass Verified</span>
                      <span className="text-[8px] font-mono text-white/55 uppercase leading-relaxed block">
                        Establishing this sovereign custom subnet and DNS-over-TLS permanently isolates your local devices from passive trace caching by Comcast, Charter, Cox, and Spectrum, securing the 45% humanitarian network pool.
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right Column (4 cols): Step Stepper & Static Guide */}
        <div className="lg:col-span-4 border-l border-white/5 lg:pl-8 space-y-6">
          <span className="text-[10px] font-mono font-black text-white/40 uppercase tracking-widest block">
            General Setup Protocol Sequence
          </span>

          <div className="space-y-4">
            {generalSteps.map((step, idx) => (
              <button
                key={idx}
                onClick={() => setActiveStep(idx)}
                className={`w-full p-4 rounded-2xl text-left border transition-all flex gap-3 items-start ${
                  activeStep === idx 
                    ? 'bg-lattice-cyan/5 border-lattice-cyan text-white shadow-md' 
                    : 'bg-white/[0.01] border-white/5 text-white/50 hover:bg-white/[0.03] hover:border-white/10'
                }`}
              >
                <div className={`w-5 h-5 rounded-full flex items-center justify-center font-mono text-[9px] font-bold shrink-0 ${
                  activeStep === idx 
                    ? 'bg-lattice-cyan text-black' 
                    : 'bg-white/5 text-white/40 border border-white/10'
                }`}>
                  {idx + 1}
                </div>
                <div className="space-y-1.5 min-w-0 flex-1">
                  <span className={`text-[10px] font-mono font-black block uppercase tracking-tight ${activeStep === idx ? 'text-lattice-cyan' : 'text-white/80'}`}>
                    {step.title}
                  </span>
                  
                  {activeStep === idx && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-2 text-[9px] leading-relaxed font-mono uppercase text-white/60 pt-1 border-t border-white/5"
                    >
                      <span>{step.desc}</span>
                      
                      {step.keyVal && (
                        <div className="p-2 bg-black/60 rounded border border-white/5 flex flex-col gap-0.5 select-all">
                          <span className="text-[7px] text-white/30 text-[8px] font-bold">{step.keyVal.key}</span>
                          <span className="text-[9px] text-lattice-cyan font-black tracking-wider">{step.keyVal.val}</span>
                        </div>
                      )}
                      
                      <div className="p-2 bg-white/5 rounded border border-white/5 text-[8px] text-white/45 italic leading-normal">
                        <strong>PRO-TIP:</strong> {step.tip}
                      </div>
                    </motion.div>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl space-y-2">
            <div className="flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5 text-lattice-cyan" />
              <span className="text-[9px] font-mono font-black text-white/80 uppercase">Need direct advice?</span>
            </div>
            <p className="text-[8px] font-mono text-white/40 uppercase leading-relaxed">
              If your hardware behaves irregularly or drops packets, visit the **Vigil** tab or click **Support** at the top right to let our sovereign assistants guide your firmware interface setup interactively via standard carrier mesh loops.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};
